import React from 'react';
import { logDepositEvent, trackCustomMetaEvent } from '@/analytics';
import Utils from '@/modules/casino/utils/Utils';
import { paymentProviderNameById } from '@/constants/paymentProvider';
import { useAppSelector } from '@/store';

export const useDepositLegacyEvents = (state: any) => {
  const profile = useAppSelector((state) => state.profile);
  const { currentCurrency } = useAppSelector((state) => state.currencies);

  const successEvent = React.useCallback(() => {
    console.table({
      deposit_currency: currentCurrency || '',
      deposit_amount: state && state.depositValue !== undefined ? state.depositValue : 0,
      event: 'success',
    });
    // Notify parent window of success
    window.parent.postMessage({ type: 'deposit-response', status: 'success' }, '*');
    // log event
    logDepositEvent('deposit_success', state);

    // Google Tag Manager
    try {
      // @ts-ignore
      if (!window.dataLayer) {
        return;
      }

      let eventType;

      // Check if profile is fully initialized
      const profileFullyInitialized =
        profile &&
        typeof profile === 'object' &&
        !profile.loading &&
        profile.ftd !== undefined &&
        profile.ftd !== 0 && // 0 is the default value for ftd (from initial state) // from core we receive ftd as null for first-time deposits or as a timestamp
        profile.client_player_id !== null &&
        profile.global_player_id !== null &&
        profile.nin !== null;

      if (profileFullyInitialized) {
        // Convert to number to handle string values
        const ftdValue = Number(profile.ftd);

        // If ftd is 0, this means this deposit is a first-time deposit
        eventType = !isNaN(ftdValue) && ftdValue === 0 ? 'ftd-success' : 'deposit-success';
      }
      // Fall back to cookie check
      else {
        const ftdTimestamp = Utils.getCookie('ftd_timestamp');

        if (ftdTimestamp !== undefined && ftdTimestamp !== null) {
          const timestampValue = Number(ftdTimestamp);

          // timestamp of 0 means this is a first-time deposit
          eventType = !isNaN(timestampValue) && timestampValue === 0 ? 'ftd-success' : 'deposit-success';
        } else {
          // Default fallback
          eventType = 'deposit-success';
        }
      }

      // Push data to GTM
      // @ts-ignore
      window.dataLayer.push({
        deposit_currency: currentCurrency || '',
        deposit_amount: state && state.depositValue !== undefined ? state.depositValue : 0,
        event: eventType,
      });
      console.table({
        deposit_currency: currentCurrency || '',
        deposit_amount: state && state.depositValue !== undefined ? state.depositValue : 0,
        event: eventType,
      });
      // Clear cookie
      if (Utils.getCookie('ftd_timestamp') !== null) {
        Utils.setCookie(
          'ftd_timestamp',
          '',
          -1, // expire instantly
          '/',
          null,
          false,
          `.${Utils.getRootDomain()}`,
        );
      }
    } catch (err) {}

    // Facebook Pixel logging
    trackCustomMetaEvent(profile.ftd ? 'web_deposit' : 'web_ftd_deposit', {
      bonus_used: state.selectedBonusInfo?.bonusId
        ? { bonus_id: state.selectedBonusInfo?.bonusId, bonus_name: state.selectedBonusInfo?.name }
        : '',
      deposit_amount: state.depositValue,
      deposit_currency: currentCurrency,
      deposit_payment_provider: state.paymentProvider,
      deposit_payment_provider_name: paymentProviderNameById[state.paymentProvider],
    });
  }, [currentCurrency, profile, state]);

  const pendingEvent = React.useCallback(() => {
    console.table({
      deposit_currency: currentCurrency || '',
      deposit_amount: state && state.depositValue !== undefined ? state.depositValue : 0,
      event: 'pending',
    });
    window.parent.postMessage({ type: 'deposit-response', status: 'pending' }, '*');

    logDepositEvent('deposit_pending', state);
  }, [currentCurrency, state]);

  const errorEvent = React.useCallback(() => {
    console.table({
      deposit_currency: currentCurrency || '',
      deposit_amount: state && state.depositValue !== undefined ? state.depositValue : 0,
      event: 'error',
    });
    window.parent.postMessage({ type: 'deposit-response', status: 'error' }, '*');
    logDepositEvent('deposit_failed', state, state.errorMessageForEvents);
  }, [currentCurrency, state]);

  const bonusesEvent = React.useCallback(() => {
    console.table({
      deposit_currency: currentCurrency || '',
      deposit_amount: state && state.depositValue !== undefined ? state.depositValue : 0,
      event: 'bonuses',
    });
    logDepositEvent('deposit_view_bonuses', state);
  }, [currentCurrency, state]);

  React.useEffect(() => {
    switch (state.currentStep) {
      case 'success':
        successEvent();
        // Handle deposit step
        break;
      case 'pending':
        pendingEvent();
        break;
      case 'error':
        errorEvent();
        break;
      case 'bonuses':
        bonusesEvent();
        break;
      default:
        // if success is found as substring in state.currentStep, call successEvent
        if (state.currentStep.includes('success')) {
          successEvent();
        } else {
          // Handle other cases if necessary
        }
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentStep]);

  return React.useMemo(
    () => ({
      successEvent,
    }),
    [successEvent],
  );
};